/**
 * TYPY ŚCIEŻKI ODCZYTU SERP-u — wspólne dla aplikacji i przyszłej funkcji `send-filter-alerts`
 * (plan E7.2, §1.68; docs/AlertEmails.md §2). Moduł `src/lib/serpRead/` jest czysty i zgodny
 * z Deno: tylko importy względne z rozszerzeniem `.ts`, zero React, zero klienta Supabase.
 *
 * Typy przeniesione tu BEZ ZMIAN z `src/hooks/useSourcePosts.ts` (`SortKey`, `SourcePostFilters`),
 * `src/lib/areaSearch.ts` (`SearchArea`) i `src/lib/filtryMiekkie.ts` (wartości filtrów miękkich);
 * tamte pliki eksportują je dalej, więc dotychczasowe importy działają jak dotąd.
 */

export type SortKey = 'newest' | 'oldest' | 'cheapest' | 'expensive';

export type SearchArea =
  | { kind: 'city'; name: string }
  | { kind: 'district'; name: string }
  | { kind: 'subdistrict'; name: string }
  | { kind: 'station'; name: string; lat: number; lon: number; radiusM: number }
  /**
   * „Blisko do…” (plan §1.18, E4.2): szkoła, praca albo adres. Filtruje jak chip stacji metra:
   * kwadrat ±`radiusM` wokół punktu. Jedno miejsce naraz (`zJednymMiejscem` w areaSearch.ts).
   */
  | { kind: 'place'; label: string; lat: number; lon: number; radiusM: number }
  | {
      kind: 'geo';
      label: string;
      lat: number;
      lon: number;
      west: number;
      south: number;
      east: number;
      north: number;
    };

export type Umeblowanie = 'umeblowane' | 'nieumeblowane';
/** „ostatnie" dojdzie, gdy agent danych da liczbę pięter budynku (dziś 144 z 4 291 ofert). */
export type Pietro = 'parter';
export type Kuchnia = 'oddzielna' | 'aneks';
export type Lazienki = '1' | '2+';
/**
 * Internet (Piotr i Maciek 28.09, bez opcji „Brak"; wdrożone 5.10): tylko „światłowód" albo
 * dowolny. MIĘKKO, jak balkon: wzmianka (`fibre_internet` w `amenities_norm`, po poprawce znacznika
 * agenta danych 20261005184312 ok. 3% ofert) = wyżej, milczenie = niżej, nic nie odpada. Twardy
 * filtr zostawiłby 3% wyników.
 */
export type Internet = 'swiatlowod';

/**
 * TYP BUDYNKU (plan E3.2, S2): słownik OGŁOSZEŃ (`flat.offers.building_type`), nie profilu.
 * Miękko, jak umeblowanie: pasujący typ wyżej, brak danych niżej, jawnie inny typ odpada.
 * `apartment` w danych znaczy „blok LUB apartamentowiec” (enrich skleja oba), stąd jedna opcja.
 */
export const TYPY_BUDYNKU = ['apartment', 'tenement', 'house', 'other'] as const;
export type TypBudynku = (typeof TYPY_BUDYNKU)[number];

/** WIEK BUDYNKU (plan E3.3): słownik ogłoszeń (`flat.offers.building_age`), miękko jak typ. */
export const WIEKI_BUDYNKU = ['new_post_2015', 'contemporary_post_2000', 'older'] as const;
export type WiekBudynku = (typeof WIEKI_BUDYNKU)[number];
/** Klucz `amenities_norm`, który oznacza światłowód — ten sam w rankingu i w ocenie karty. */
export const KLUCZ_SWIATLOWODU = 'fibre_internet';
/** Klucz `amenities_norm` parkingu podziemnego (`flat.parking_z_tekstu`, migracja 20261006215126). */
export const KLUCZ_GARAZU = 'garage';

/**
 * UDOGODNIENIA (1b cz. 2, Piotr 28.09: miękko). Klucze `amenities_norm`, w kolejności pokrycia
 * (pomiar 28.09 na aktywnych: pralka 3 094 … biurko 313). `parking` obejmuje garaż — tak liczy
 * baza. Nic nie odpada: `amenities_norm` zapisuje tylko „jest", brak wzmianki to „nie wiadomo".
 * Kolejność robi `offers_ranked` (`p_prefs.amenities`: liczba brakujących rozstrzyga remisy).
 * `fibre_internet` ma własny filtr „Internet: światłowód” (`Internet`), nie jest na tej liście.
 */
export const UDOGODNIENIA = [
  'washing_machine', 'fridge', 'parking', 'oven', 'dishwasher', 'tv',
  'air_conditioning', 'basement', 'garden', 'desk_home_office',
] as const;
export type Udogodnienie = (typeof UDOGODNIENIA)[number];

export interface SourcePostFilters {
  postingTypes?: Array<'individual' | 'agency' | 'unclear'>;
  /** Legacy district list (old links / programmatic callers). The area bar
   * uses `areas` instead. */
  suburbs?: string[];
  /** Area chips: districts (name match) + estates (bbox over coords). */
  areas?: SearchArea[];
  rentMin?: number;
  rentMax?: number;
  /** Which column rentMin/Max (and price sorts) apply to: advertised rent or
   * rent + known utilities (view column total_monthly_cost_pln). */
  costMode?: 'rent' | 'total';
  /** Room-count buttons; 4 means "4+". Empty/undefined = all. */
  rooms?: number[];
  areaMin?: number;
  areaMax?: number;
  /** ≤10 min walk to a metro station (metro_nearest_m <= 800). */
  nearMetro?: boolean;
  /** POI score chips — each requires the matching score >= 70. */
  poi?: Array<'transit' | 'walk' | 'green'>;
  postedSinceHours?: number | null;
  /** Debug lens: include confirmed-dead (stale_at set) + sold offers, which are
   * hidden by default. */
  includeDead?: boolean;
  /** Debug lens: only offers MISSING a price (extracted_rent_pln is null). */
  noPrice?: boolean;
  /** Debug lens: only offers WITHOUT exact coords — these fall back to the
   * district centroid on the map (extracted_lat is null). */
  approxLocation?: boolean;
  /** Debug lens: only the data-entry typo outliers normally HIDDEN by the sanity
   * guard (area outside [AREA_SANITY_MIN, AREA_SANITY_MAX] or rent > RENT_SANITY_MAX).
   * For reviewing the moderation backlog from the SERP until the backoffice queue
   * exists. */
  sanityOutliers?: boolean;
  /**
   * SZUKAM ZE ZWIERZĘCIEM — JEDYNY filtr zwierząt (decyzja Piotra 28.09: zwierzęta miękko).
   * Ustawia go i start z profilu najemcy (SRCH-01), i przełącznik w arkuszu filtrów; do
   * 28.09 były to dwa stany, a przełącznik był TWARDY (tylko jawne „tak": 627 z 6 190
   * ofert, −90%). Odcinamy wyłącznie jawne „nie"; oferty bez deklaracji zostają, a karta
   * mówi wprost, że trzeba zapytać. Podnoszenie „tak" na górę robi `flat.offers_ranked`.
   */
  zeZwierzeciem?: boolean;
  /**
   * FILTRY MIĘKKIE etapu 1b (ustalenia 28.09): spełnia → wyżej, nie wiadomo → niżej z adnotacją,
   * wprost nie spełnia → wypada. Znaczenie wartości, predykaty i adnotację trzyma
   * `src/lib/filtryMiekkie.ts`.
   */
  umeblowanie?: Umeblowanie;
  pietro?: Pietro;
  kuchnia?: Kuchnia;
  lazienki?: Lazienki;
  balkon?: boolean;
  /** Winda: jawne „bez windy" wypada, brak danych zostaje (`has_elevator is not false`). */
  winda?: boolean;
  /** Kaucja maksymalnie X zł: odpada tylko kaucja ZNANA i wyższa (`deposit_pln is null or <= X`). */
  kaucjaMax?: number;
  /** Udogodnienia (miękko: tylko kolejność, nic nie odpada) — klucze `amenities_norm`. */
  udogodnienia?: Udogodnienie[];
  /** Internet: „światłowód" (miękko: tylko kolejność, nic nie odpada); brak = dowolny. */
  internet?: Internet;
  /**
   * Podfiltr „w garażu podziemnym” pod „Parking lub garaż” (Piotr 6.10, wrócił 18.10): miękko,
   * token `garage` w `amenities_norm` (parking podziemny, `flat.parking_z_tekstu`), nic nie odpada.
   * Ma sens tylko z parkingiem: zdjęcie parkingu zdejmuje i jego. W dokumencie Moich filtrów
   * `underground_garage` (migracja 20261018205012).
   */
  garaz?: boolean;
  /**
   * Świadome wyłączenie reguły SRCH-15 na czas tego wyszukiwania: pokaż także oferty bez
   * informacji o opłatach. Ustawia to JEDNA linijka przy liczniku, która pojawia się
   * tylko wtedy, gdy reguła zostawiła co najwyżej dziesięć ofert — czyli wtedy, gdy
   * człowiek patrzy na pustkę i musi wiedzieć, że da się ją cofnąć.
   */
  nieznaneOplaty?: boolean;
  /**
   * „Pokaż oferty bez zdjęć” (decyzja Piotra 29.09): domyślnie WŁĄCZONE (`undefined`).
   * `false` = filtr twardy `ma_zdjecia = true` na liście, pinach i liczniku; kolejności nie
   * zmienia (oferty bez zdjęć i tak stoją na końcu, za drugą kreską).
   */
  bezZdjec?: boolean;
  /**
   * Tylko oferty BEZ PUNKTU na mapie (pastylka „N ofert bez adresu · pokaż na liście”, V1 29.09):
   * `lat is null`. Te oferty nie mają znacznika, więc to jedyna droga do nich z mapy.
   */
  bezAdresu?: boolean;
  /**
   * „Od zaraz" — mieszkania wolne TERAZ. Czyta tę samą parę co `src/lib/availability.ts`
   * (`available_from` + `available_immediately`) i tak samo rozstrzyga remis: gdy jest data,
   * wygrywa data.
   */
  availableNow?: boolean;
  /** Typ budynku (miękko) — wartość ze słownika ogłoszeń, patrz `TypBudynku`. */
  typBudynku?: TypBudynku;
  /** Wiek budynku (miękko) — wartość ze słownika ogłoszeń, patrz `WiekBudynku`. */
  wiekBudynku?: WiekBudynku;
  /**
   * „Właściciel na FlatHero” (A5, plan §1.16): każdy właściciel na platformie, zweryfikowany czy
   * nie — `origin <> 'scraped'`. Filtr TWARDY (Ogłoszenie): oferta ze scrapingu odpada.
   */
  wlascicielNaFlatHero?: boolean;
  /**
   * „Ulubione” (A6, plan §1.14): tylko oferty z serduszkiem oglądającego. Filtr LISTY, nie
   * kryterium — nigdy w zapisanym zestawie ani w e-mailach (`UNSAVED_FILTER_KEYS`).
   */
  ulubione?: boolean;
  /**
   * Identyfikatory ulubionych oglądającego — dokłada je strona (`zUlubionymi`) z `useFavorites`
   * (konto przez RLS `flat.favorites` albo lista w przeglądarce). Tylko w pamięci: nie trafia do
   * adresu ani do dokumentu. `ulubione` bez tej listy = zero ofert (bezpieczny kierunek).
   */
  ulubioneIds?: string[];
  /** Final ordering of cluster heads. Default = 'newest'. */
  sort?: SortKey;
  /** SERP rendering mode. 'list' (default) = card grid; 'map' = Leaflet map view. */
  view?: 'list' | 'map';
  /** Draw-your-own-area: a closed polygon of [lat, lon] vertices. When set, the
   * SERP reads through flat.offers_in_polygon (geog ∈ polygon) instead of selecting
   * flat.offers directly; all other filters/sort/pagination apply identically.
   *
   * The predecessor named here — flat.serp_heads_in_polygon over the legacy matview —
   * was dropped in Phase 4 (20260728061057). Leaving its name in this comment cost
   * real diagnostic time once; it is not a synonym for the current path. */
  polygon?: [number, number][];
}
